import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from './Logo';
import { LanguageSelector } from './LanguageSelector';
import { Link, useNavigate, NavLink } from 'react-router-dom';

export const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isRTL = i18n.language === 'ar';

  const menuVariants = {
    closed: { height: 0, opacity: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
    open: { height: 'auto', opacity: 1, transition: { duration: 0.3, ease: 'easeInOut' } }
  };

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-stone-900/95 backdrop-blur-sm shadow-lg py-2' : 'bg-stone-900/80 backdrop-blur-sm py-4'
      }`}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center w-full min-h-[64px] flex-wrap gap-y-2">
          {/* Logo */}
          <motion.div 
            className="flex items-center flex-shrink-0 h-full"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <div 
              className="cursor-pointer flex items-center h-full" 
              onClick={() => navigate('/')}
            >
              <Logo />
            </div>
          </motion.div>

          {/* Desktop Navigation Links */}
          <div className="hidden md:flex flex-1 justify-end items-center gap-4 md:gap-8 h-full min-w-0">
            <NavLink to="/" end className={({ isActive }) => `text-white flex items-center h-full px-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${isActive ? 'border-b-2 border-primary text-primary font-bold bg-white/10' : 'hover:text-primary hover:bg-white/10'}`} onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>{t('nav.home')}</NavLink>
            <NavLink to="/about" className={({ isActive }) => `text-white flex items-center h-full px-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${isActive ? 'border-b-2 border-primary text-primary font-bold bg-white/10' : 'hover:text-primary hover:bg-white/10'}`} onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>{t('nav.about')}</NavLink>
            <NavLink to="/history" className={({ isActive }) => `text-white flex items-center h-full px-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${isActive ? 'border-b-2 border-primary text-primary font-bold bg-white/10' : 'hover:text-primary hover:bg-white/10'}`} onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>{t('nav.history')}</NavLink>
            <NavLink to="/contact" className={({ isActive }) => `text-white flex items-center h-full px-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ${isActive ? 'border-b-2 border-primary text-primary font-bold bg-white/10' : 'hover:text-primary hover:bg-white/10'}`} onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>{t('nav.contact')}</NavLink>
          </div>

          {/* Language Selector (desktop only) */}
          <div className="hidden md:flex items-center ml-4">
            <LanguageSelector />
          </div>

          {/* Hamburger Button (mobile only) */}
          <div className="flex md:hidden items-center ml-auto gap-2">
            <button
              className="flex items-center justify-center p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary bg-transparent"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Open menu"
              style={{ marginLeft: 'auto' }}
            >
              {isMenuOpen ? <X size={24} className="text-white" /> : <Menu size={24} className="text-white" />}
            </button>
            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden bg-stone-900/95 backdrop-blur-sm overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col space-y-4 items-center">
              <div className="w-full flex flex-col items-center gap-4">
                <Link to="/" onClick={() => { setIsMenuOpen(false); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="text-white w-full text-center py-2 rounded hover:bg-primary/20">{t('nav.home')}</Link>
                <Link to="/about" onClick={() => { setIsMenuOpen(false); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="text-white w-full text-center py-2 rounded hover:bg-primary/20">{t('nav.about')}</Link>
                <Link to="/history" onClick={() => { setIsMenuOpen(false); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="text-white w-full text-center py-2 rounded hover:bg-primary/20">{t('nav.history')}</Link>
                <Link to="/contact" onClick={() => { setIsMenuOpen(false); window.scrollTo({top: 0, behavior: 'smooth'}); }} className="text-white w-full text-center py-2 rounded hover:bg-primary/20">{t('nav.contact')}</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};